import { useEffect, useRef, useState } from 'react';

// 签名生成函数，使用浏览器内置的 SubtleCrypto API
async function genSignature(signingContent: string, accessToken: string) {
  // 将 accessToken 转换为 ArrayBuffer 格式
  const key = new TextEncoder().encode(accessToken);
  // 使用 SubtleCrypto 导入密钥
  const cryptoKey = await window.crypto.subtle.importKey('raw', key, { name: 'HMAC', hash: 'SHA-256' }, false, [
    'sign',
  ]);
  // 将签名内容转换为 ArrayBuffer 格式
  const data = new TextEncoder().encode(signingContent);
  // 生成签名
  const signature = await window.crypto.subtle.sign('HMAC', cryptoKey, data);
  // 将签名转换为 Base64 字符串并进行 URI 编码
  const hashInBase64 = btoa(String.fromCharCode(...new Uint8Array(signature)));
  const encodeSign = encodeURIComponent(hashInBase64);
  return `&signature=${encodeSign}`;
}

// URL生成函数
async function genReqURL(parameter: { [key: string]: string | number }, accessToken: string, baseURL: string) {
  let signingContent = '';
  const pathKey = Object.keys(parameter).sort();
  for (const k of pathKey) {
    if (signingContent !== '') {
      signingContent += '&';
    }
    signingContent += `${k}=${parameter[k]}`;
  }
  const signature = await genSignature(signingContent, accessToken);
  return `${baseURL}?${signingContent}${signature}`;
}

const getSignedUrl = async (url: string, requestid: string) => {
  const appkey = 'e5b4cf910e0c4cd6882555e6ac9f20e0';
  const accessToken = '0c540aa580684e2596d99c10b7efbb60';

  const parameter = {
    appkey,
    timestamp: Math.floor(Date.now() / 1000), // 当前时间戳（秒）
    requestid,
  };

  try {
    const signedUrl = await genReqURL(parameter, accessToken, url);
    return signedUrl;
  } catch (error) {
    console.log('Error generating signed URL:', error);
    throw error;
  }
};

export const useDigitalHuman = () => {
  const VirtualmanProjectId = 'a00d31747a684b81abbc3cb1c502a532';
  const Protocol = 'webrtc';
  const DriverType = 1;

  const [canUseDigitalHuman, setCanUseDigitalHuman] = useState<boolean>(true);
  const [digitalHumanReady, setDigitalHumanReady] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string>('');

  const [reqId, setReqId] = useState<string>('');
  const [userId, setUserId] = useState<string>('');

  const [playStreamAddr, setPlayStreamAddr] = useState<string>('');
  const websocketRef = useRef<WebSocket | null>(null);

  const [closeSessionData, setCloseSessionData] = useState<{
    url: string;
    data: any;
  }>({
    url: '',
    data: {},
  });

  const init = async ({ _reqId, _userId }: { _reqId: string; _userId: string }) => {
    try {
      setReqId(_reqId);
      setUserId(_userId);
      const signedUrl = await getSignedUrl(
        'https://gw-sg.tvs.qq.com/v2/ivh/sessionmanager/sessionmanagerservice/createsession',
        _reqId
      );
      // 使用生成的签名URL进行实际的API请求
      const response = await fetch(signedUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json;charset=utf-8',
        },
        body: JSON.stringify({
          Header: {},
          Payload: {
            ReqId: _reqId,
            VirtualmanProjectId,
            UserId: _userId,
            Protocol,
            DriverType,
          },
        }),
      });

      const data = await response.json();
      if (data.Header.Code === 0) {
        setSessionId(data.Payload.SessionId);
        setCloseSessionData({
          url: await getSignedUrl(
            'https://gw-sg.tvs.qq.com/v2/ivh/sessionmanager/sessionmanagerservice/closesession',
            _reqId
          ),
          data: {
            Header: {},
            Payload: {
              ReqId: _reqId,
              SessionId: data.Payload.SessionId,
              UserId: _userId,
              VirtualmanProjectId,
              Protocol,
              DriverType,
            },
          },
        });
      } else {
        setCanUseDigitalHuman(false);
      }
    } catch (error) {
      console.log('Init Error:', error);
    }
  };

  useEffect(() => {
    if (!reqId || !userId || !sessionId) return;
    checkChatStatus();
  }, [reqId, userId, sessionId]);

  const checkChatStatus = async () => {
    try {
      const signedUrl = await getSignedUrl(
        'https://gw-sg.tvs.qq.com/v2/ivh/sessionmanager/sessionmanagerservice/statsession',
        reqId
      );

      // 使用生成的签名URL进行实际的API请求
      const requestData = {
        Header: {},
        Payload: {
          ReqId: reqId,
          SessionId: sessionId,
          DriverType,
          Protocol,
          VirtualmanProjectId,
          UserId: userId,
        },
      };

      const response = await fetch(signedUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json;charset=utf-8',
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        const data = await response.json();
        // setChatStatus(responseData.SessionStatus);
        // 会话状态。1：进行中(已就绪)，2：已关闭，3：准备中(未就绪)，4：创建会话失败
        if (data.Payload.SessionStatus !== 1) {
          setTimeout(() => {
            checkChatStatus();
          }, 2000);
        } else {
          setPlayStreamAddr(data.Payload.PlayStreamAddr);
          startSession();
        }
      }
    } catch (error) {
      console.log('CheckChatStatus Error:', error);
    }
  };

  const startSession = async () => {
    try {
      const signedUrl = await getSignedUrl(
        'https://gw-sg.tvs.qq.com/v2/ivh/sessionmanager/sessionmanagerservice/startsession',
        reqId
      );
      // 使用生成的签名URL进行实际的API请求
      const requestData = {
        Header: {},
        Payload: {
          ReqId: reqId,
          SessionId: sessionId,
        },
      };
      const response = await fetch(signedUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json;charset=utf-8',
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        await openWebSocket();
      }
    } catch (error) {
      console.log('StartSession Error:', error);
    }
  };

  const openWebSocket = async (callback?: () => void) => {
    try {
      // 请求本地服务器生成WebSocket签名URL
      const websocketUrl = await getSignedUrl(
        'wss://gw-sg.tvs.qq.com/v2/ws/ivh/interactdriver/interactdriverservice/commandchannel',
        sessionId
      );

      // 创建WebSocket连接
      const ws = new WebSocket(websocketUrl);

      ws.onopen = function () {
        console.log('WebSocket opened');
        // 可以开始对话了
        setDigitalHumanReady(true);
        callback && callback();
      };

      ws.onmessage = function (event) {
        // console.log('收到消息:', event.data);
      };

      ws.onclose = function () {
        console.log('WebSocket closed');
      };

      ws.onerror = function (error) {
        // console.log('WebSocket错误:', error);
      };

      websocketRef.current = ws;
    } catch (error) {
      console.log('WebSocket error:', error);
    }
  };

  const sendTextMessage = (text: string) => {
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
      const requestData = {
        Header: {},
        Payload: {
          ReqId: reqId,
          SessionId: sessionId,
          Command: 'SEND_TEXT',
          Data: {
            Text: text,
            ChatCommand: 'NotUseChat',
          },
        },
      };

      websocketRef.current.send(JSON.stringify(requestData));
    } else {
      openWebSocket(() => sendTextMessage(text));
    }
  };

  const closeSession = async () => {
    try {
      if (!sessionId) return;
      const signedUrl = await getSignedUrl(
        'https://gw-sg.tvs.qq.com/v2/ivh/sessionmanager/sessionmanagerservice/closesession',
        reqId
      );

      // 使用生成的签名URL进行实际的API请求
      const requestData = {
        Header: {},
        Payload: {
          ReqId: reqId,
          SessionId: sessionId,
          UserId: userId,
          VirtualmanProjectId,
          Protocol,
          DriverType,
        },
      };

      const response = await fetch(signedUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json;charset=utf-8',
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        // const responseData = await response.json();
      }
    } catch (error) {
      console.log('CloseSession Error:', error);
    }
  };

  return {
    playStreamAddr,
    digitalHumanReady,
    init,
    checkChatStatus,
    startSession,
    sendTextMessage,
    closeSession,
    canUseDigitalHuman,
    closeSessionData,
  };
};
